import { useEffect, useState } from "react";
import { useUser } from "../hooks/useQueries";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "../components/shared/Input";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export default function Login() {
  const navigate = useNavigate();

  const {
    login,
    isLoginLoading,
    verifyOtp,
    isVerifyOtpLoading,
    establishSession,
    verifyEmail,
  } = useUser();

  // { email, password, rememberMe } captured at step 1, needed again at step 2.
  const [credentials, setCredentials] = useState(null);
  // Server response from step 1 when an emailed code is required.
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const validationSchema = yup.object().shape({
    password: yup.string().required("Required"),
    email: yup.string().email().required("Required"),
    rememberMe: yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { email: "", password: "", rememberMe: false },
  });

  const registerClickHandler = () => {
    navigate("/register");
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get("token");

    if (token) {
      verifyEmail(token);
    }
  }, []);

  const handleLoginResult = (data) => {
    if (data?.requiresTwoFactor) {
      setOtp("");
      setChallenge({
        maskedEmail: data.maskedEmail,
        otpExpiresAt: data.otpExpiresAt,
        resendAfterSeconds: data.resendAfterSeconds ?? 0,
      });
    } else if (data?.jwtToken) {
      // trusted device or 2FA disabled — straight in
      establishSession(data);
    }
  };

  const onSubmit = (data, e) => {
    const payload = {
      email: data.email,
      password: data.password,
      rememberMe: data.rememberMe,
    };
    setCredentials(payload);
    login(payload, { onSuccess: handleLoginResult });
  };

  const onVerifyOtp = (e) => {
    e?.preventDefault();
    if (!credentials || isVerifyOtpLoading) return;

    verifyOtp(
      { email: credentials.email, otp: otp, rememberMe: credentials.rememberMe },
      {
        onSuccess: (data) => {
          if (data?.jwtToken) {
            establishSession(data);
          }
        },
      }
    );
  };

  const resendCode = (e) => {
    e?.preventDefault();
    if (!credentials || isLoginLoading || resendIn > 0) return;

    login({ ...credentials }, { onSuccess: handleLoginResult });
  };

  const useDifferentAccount = () => {
    setChallenge(null);
    setCredentials(null);
    setOtp("");
  };

  // Count down the server-side resend cooldown.
  useEffect(() => {
    setResendIn(challenge?.resendAfterSeconds ?? 0);
  }, [challenge]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-2xl font-medium leading-9 tracking-tight text-gray-900">
          {challenge ? "Check your email" : "Log in to your account"}
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
          {challenge ? (
            <form className="space-y-6" onSubmit={onVerifyOtp}>
              <div role="status" className="rounded-md bg-primary-50 p-4 ring-1 ring-inset ring-primary-100">
                <p className="text-sm text-gray-700">
                  We sent a 6-digit code to <span className="font-semibold text-gray-900">{challenge.maskedEmail}</span>.
                  Enter it below to finish signing in.
                </p>
              </div>

              <div>
                <Input
                  label="Verification code"
                  elementtype="input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  name="otp"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isVerifyOtpLoading || otp.length !== 6}
                  aria-busy={isVerifyOtpLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isVerifyOtpLoading && (
                    <span
                      className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                      aria-hidden="true"
                    />
                  )}
                  {isVerifyOtpLoading ? "Verifying…" : "Verify & log in"}
                </button>
              </div>

              <div className="flex items-center justify-between text-sm leading-6">
                <button
                  type="button"
                  disabled={resendIn > 0 || isLoginLoading}
                  className="inline-flex items-center gap-1.5 font-semibold text-primary hover:text-primary-400 disabled:cursor-not-allowed disabled:text-gray-400"
                  onClick={resendCode}
                >
                  <ArrowPathIcon className={`h-4 w-4 ${isLoginLoading ? "animate-spin" : ""}`} aria-hidden="true" />
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : isLoginLoading ? "Sending…" : "Resend code"}
                </button>

                <button
                  type="button"
                  className="font-medium text-gray-500 hover:text-gray-700"
                  onClick={useDifferentAccount}
                >
                  Use a different account
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <Input label="Email address" elementtype="input" type="email" name="email" {...register("email")} error={errors.email} />
                {errors.email && <span className="text-red-500 text-xs">{errors.email.message}</span>}
              </div>

              <div>
                <Input label="Password" elementtype="input" type="password" {...register("password")} error={errors.password} />
                {errors.password && <span className="text-red-500 text-xs">{errors.password.message}</span>}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-400"
                    {...register("rememberMe")}
                  />
                  <label htmlFor="remember-me" className="ml-3 block text-sm leading-6 text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm leading-6">
                  <NavLink to="/forgot-password" className="font-semibold text-primary hover:text-primary-400">
                    Forgot password?
                  </NavLink>
                </div>
              </div>

              <p className="text-xs leading-5 text-gray-500">
                For your security we will email you a verification code when you sign in from a new device.
                Remember me skips this step on this device.
              </p>

              <div>
                <button
                  type="submit"
                  disabled={isLoginLoading}
                  aria-busy={isLoginLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoginLoading && (
                    <span
                      className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                      aria-hidden="true"
                    />
                  )}
                  {isLoginLoading ? "Logging in…" : "Log in"}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500">
          Not a member?{" "}
          <a className="font-semibold leading-6 text-primary hover:text-primary-400 cursor-pointer" onClick={registerClickHandler}>
            Register{" "}
          </a>
        </p>
      </div>
    </div>
  );
}
